// Hook for accessing organization context and management functions

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth0Login } from "./useAuth0Login";
import { useAuthenticatedUser } from "./useAuthenticatedUser";
import { apiClient } from "~/lib/orpc-client";

const QUERY_KEYS = {
  MY_ORGANIZATION: ["my-organization"],
  ORGANIZATIONS: ["organizations"],
  ORGANIZATION: (id: string) => ["organization", id],
  ORGANIZATION_MEMBERS: (id: string) => ["organization-members", id],
};

/**
 * Hook for accessing the current user's organization context
 */
export function useMyOrganization() {
  const { isAuthenticated, orgId } = useAuth0Login();
  const { isPublisherAccount, organizationId } = useAuthenticatedUser();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: QUERY_KEYS.MY_ORGANIZATION,
    queryFn: () => apiClient.organizations.getMyOrganization(),
    // orgId: from Auth0 JWT (org-specific login flow only)
    // organizationId: from /users/me (covers normal login for org members)
    enabled: isAuthenticated && (!!orgId || !!organizationId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    organization: data?.organization ?? null,
    organizations: data?.organizations ?? [],
    isLoading,
    error,
    refetch,
    // Quick access to org info from Auth0 token
    orgId,
    isOrgMember: isPublisherAccount,
  };
}

/**
 * Hook for listing all organizations (admin only)
 */
export function useOrganizations() {
  const { isAuthenticated, hasRole } = useAuth0Login();
  const isAdmin = hasRole("ADMIN");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: QUERY_KEYS.ORGANIZATIONS,
    queryFn: () => apiClient.organizations.list(),
    enabled: isAuthenticated && isAdmin,
  });

  return {
    organizations: data?.organizations ?? [],
    isLoading,
    error,
    refetch,
  };
}

/**
 * Hook for getting a specific organization
 */
export function useOrganization(orgId: string | undefined) {
  const { isAuthenticated } = useAuth0Login();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: QUERY_KEYS.ORGANIZATION(orgId ?? ""),
    queryFn: () => apiClient.organizations.getById({ orgId: orgId! }),
    enabled: isAuthenticated && !!orgId,
  });

  return {
    organization: data?.organization ?? null,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Hook for listing organization members
 */
export function useOrganizationMembers(orgId: string | undefined) {
  const { isAuthenticated } = useAuth0Login();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: QUERY_KEYS.ORGANIZATION_MEMBERS(orgId ?? ""),
    queryFn: () => apiClient.organizations.listMembers({ orgId: orgId! }),
    enabled: isAuthenticated && !!orgId,
  });

  return {
    members: data?.members ?? [],
    isLoading,
    error,
    refetch,
  };
}

/**
 * Hook for organization management mutations
 */
export function useOrganizationMutations() {
  const queryClient = useQueryClient();

  // Admin-only: create organization
  const createOrganization = useMutation({
    mutationFn: (data: { name: string; displayName: string }) =>
      apiClient.organizations.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORGANIZATIONS });
    },
  });

  // Self-service: create your own organization (any authenticated user)
  const createMyOrganization = useMutation({
    mutationFn: (data: { name: string; displayName: string }) =>
      apiClient.organizations.createMyOrg(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MY_ORGANIZATION });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORGANIZATIONS });
    },
  });

  const inviteToOrganization = useMutation({
    mutationFn: ({ orgId, email, inviterName }: { orgId: string; email: string; inviterName?: string }) =>
      apiClient.organizations.invite({ orgId, email, inviterName }),
  });

  const addMember = useMutation({
    mutationFn: ({ orgId, userId }: { orgId: string; userId: string }) =>
      apiClient.organizations.addMember({ orgId, userId }),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORGANIZATION_MEMBERS(orgId) });
    },
  });

  const removeMember = useMutation({
    mutationFn: ({ orgId, userId }: { orgId: string; userId: string }) =>
      apiClient.organizations.removeMember({ orgId, userId }),
    onSuccess: (_, { orgId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORGANIZATION_MEMBERS(orgId) });
    },
  });

  return {
    createOrganization,
    createMyOrganization,
    inviteToOrganization,
    addMember,
    removeMember,
  };
}
